/**
 * Gamepad API helpers shared by the Controller (per-frame reads) and the
 * settings overlay (device status, joystick axis assignment).
 *
 * Two kinds of device:
 * - **gamepad** — anything the browser exposes with `mapping === 'standard'`,
 *   i.e. an Xbox-layout pad. Indices are fixed by the spec.
 * - **joystick** — anything else. Flight sticks have no standard mapping, and
 *   the same stick reports its axes at different indices per browser (Chrome
 *   puts an Extreme 3D Pro's twist on axis 5 and its hat on axis 9 as a single
 *   "POV" value; Firefox numbers them differently). So the layout is a small
 *   `{ pitch, roll, yaw, throttle, hat }` table: a browser default for the
 *   Extreme 3D Pro, overridable by the player's own assignment (persisted as
 *   the `joystickAxes` setting).
 *
 * Browsers only reveal a gamepad after one of its buttons is pressed, and only
 * over a secure origin (localhost counts) — hence the "press any button" status.
 */

const EXTREME_3D = /046d.*c215|extreme 3d/i;

/** Every connected pad (the API returns a sparse array with nulls). */
export function listPads() {
  if (typeof navigator === 'undefined' || !navigator.getGamepads) return [];
  return Array.from(navigator.getGamepads()).filter(Boolean);
}

/** The first connected device of a kind ('gamepad' | 'joystick'), or null. */
export function findDevice(kind) {
  const pads = listPads();
  if (kind === 'gamepad') return pads.find((p) => p.mapping === 'standard') || null;
  // Prefer a recognised stick, then anything non-standard.
  return pads.find((p) => p.mapping !== 'standard' && EXTREME_3D.test(p.id))
    || pads.find((p) => p.mapping !== 'standard')
    || null;
}

/** A readable name for a pad — browsers append vendor/product noise to `id`. */
export function deviceLabel(pad) {
  if (!pad) return '';
  if (EXTREME_3D.test(pad.id)) return 'Logitech Extreme 3D Pro';
  return pad.id.replace(/\s*\(.*\)\s*/g, ' ').replace(/\s+/g, ' ').trim() || 'Controller';
}

const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);

/**
 * Default joystick layout. Each axis is `{ i, s }` — index and sign, oriented
 * so +1 is pull / right / twist right / full throttle. `hat` is a single POV
 * axis (`{ i }`) or null.
 */
export function defaultJoystickLayout() {
  if (isFirefox) {
    return {
      pitch: { i: 1, s: 1 },
      roll: { i: 0, s: 1 },
      yaw: { i: 2, s: 1 },
      throttle: { i: 3, s: -1 },
      hat: null,
    };
  }
  return {
    pitch: { i: 1, s: 1 },
    roll: { i: 0, s: 1 },
    yaw: { i: 5, s: 1 },
    throttle: { i: 6, s: -1 },
    hat: { i: 9 },
  };
}

/** Validate a persisted layout; returns it, or undefined to use the default. */
export function validJoystickLayout(v) {
  if (!v || typeof v !== 'object') return undefined;
  const axis = (a) => a && Number.isInteger(a.i) && a.i >= 0 && a.i < 32 && (a.s === 1 || a.s === -1);
  if (!axis(v.pitch) || !axis(v.roll) || !axis(v.yaw) || !axis(v.throttle)) return undefined;
  const hat = v.hat && Number.isInteger(v.hat.i) && v.hat.i >= 0 && v.hat.i < 32 ? { i: v.hat.i } : null;
  const pick = (a) => ({ i: a.i, s: a.s });
  return { pitch: pick(v.pitch), roll: pick(v.roll), yaw: pick(v.yaw), throttle: pick(v.throttle), hat };
}

/**
 * Decode a POV hat axis: 0..7 clockwise from up, or -1 when centred. Chrome
 * reports the eight directions as −1 + k·2/7 and centred as ≈1.29.
 */
export function decodePov(v) {
  if (v === undefined || v > 1.05 || v < -1.05) return -1;
  const k = Math.round((v + 1) * 3.5);
  return k >= 0 && k <= 7 ? k : -1;
}

/** Radial deadzone + expo on a 2-axis stick. Writes into `out` = [x, y]. */
export function shapeStick(x, y, deadzone, expo, out) {
  const mag = Math.hypot(x, y);
  if (mag <= deadzone) { out[0] = 0; out[1] = 0; return out; }
  const m = Math.min(1, (mag - deadzone) / (1 - deadzone));
  const shaped = (1 - expo) * m + expo * m * m * m;
  out[0] = (x / mag) * shaped;
  out[1] = (y / mag) * shaped;
  return out;
}

/** Deadzone + expo on a single axis. */
export function shapeAxis(v, deadzone, expo) {
  const a = Math.abs(v);
  if (a <= deadzone) return 0;
  const m = Math.min(1, (a - deadzone) / (1 - deadzone));
  return Math.sign(v) * ((1 - expo) * m + expo * m * m * m);
}

export function buttonDown(b) {
  return !!b && (b.pressed || b.value > 0.5);
}

/**
 * Step-by-step joystick axis assignment: each step asks the player to move one
 * control, then records whichever axis moved furthest from where it rested when
 * the step began (and which way). Driven by `requestAnimationFrame` so it works
 * from the start screen with no scenario running.
 */
export const CALIBRATION_STEPS = [
  { key: 'pitch', prompt: 'Pull the stick fully BACK' },
  { key: 'roll', prompt: 'Push the stick fully RIGHT' },
  { key: 'yaw', prompt: 'Twist the stick fully RIGHT' },
  { key: 'throttle', prompt: 'Throttle to IDLE first, then sweep it to FULL' },
  { key: 'hat', prompt: 'Push the hat switch UP (or Skip)', optional: true },
];

export class AxisCalibrator {
  constructor({ onStep, onDone, onError }) {
    this.onStep = onStep;
    this.onDone = onDone;
    this.onError = onError;
    this._raf = 0;
    this._step = 0;
    this._result = {};
    this._used = new Set();
    this._baseline = null;
    this._settle = 0;
  }

  start() {
    const pad = findDevice('joystick');
    if (!pad) { this.onError?.('No joystick found — press a button on it, then try again.'); return; }
    this._index = pad.index;
    this._beginStep(0);
    const tick = () => { this._tick(); if (this._raf) this._raf = requestAnimationFrame(tick); };
    this._raf = requestAnimationFrame(tick);
  }

  cancel() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = 0;
  }

  skip() {
    const step = CALIBRATION_STEPS[this._step];
    if (!step?.optional) return;
    this._result[step.key] = null;
    this._next();
  }

  _pad() {
    return listPads().find((p) => p.index === this._index) || null;
  }

  _beginStep(n) {
    this._step = n;
    this._baseline = null;
    this._settle = 0;
    this.onStep?.(CALIBRATION_STEPS[n], n, CALIBRATION_STEPS.length);
  }

  _next() {
    if (this._step + 1 >= CALIBRATION_STEPS.length) {
      this.cancel();
      this.onDone?.(validJoystickLayout(this._result) ? this._result : null);
      return;
    }
    this._beginStep(this._step + 1);
  }

  _tick() {
    const pad = this._pad();
    if (!pad) { this.cancel(); this.onError?.('The joystick was disconnected.'); return; }
    const axes = pad.axes;
    // Give the player's hand a moment off the previous control before
    // sampling where everything rests.
    if (!this._baseline) {
      this._settle += 1;
      if (this._settle < 20) return;
      this._baseline = Array.from(axes);
      return;
    }
    const step = CALIBRATION_STEPS[this._step];
    // The throttle rests anywhere, so it's found by a sweep rather than a
    // deflection from rest: the first axis to cover most of its travel wins,
    // and whichever end it finished at is "full".
    if (step.key === 'throttle') {
      if (!this._lo) { this._lo = Array.from(this._baseline); this._hi = Array.from(this._baseline); }
      for (let i = 0; i < axes.length; i++) {
        if (this._used.has(i)) continue;
        this._lo[i] = Math.min(this._lo[i], axes[i]);
        this._hi[i] = Math.max(this._hi[i], axes[i]);
        if (this._hi[i] - this._lo[i] > 1.4) {
          this._used.add(i);
          this._result.throttle = { i, s: axes[i] > (this._lo[i] + this._hi[i]) / 2 ? 1 : -1 };
          this._lo = this._hi = null;
          this._next();
          return;
        }
      }
      return;
    }
    let best = -1, bestDelta = 0;
    for (let i = 0; i < axes.length; i++) {
      if (this._used.has(i)) continue;
      const d = axes[i] - this._baseline[i];
      if (Math.abs(d) > Math.abs(bestDelta)) { best = i; bestDelta = d; }
    }
    if (best < 0 || Math.abs(bestDelta) < 0.5) return;
    this._used.add(best);
    this._result[step.key] = step.key === 'hat' ? { i: best } : { i: best, s: bestDelta > 0 ? 1 : -1 };
    this._next();
  }
}
